import {
  KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode,
  forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import type { CatalogEntry } from '../../../api/admin';
import type { ConnectionRow, WorkflowEdge, WorkflowGraph, WorkflowNode } from '../../../api/forms';
import { RowMenu } from '../../../components/fs';
import { describeGroup } from '../ConnectionEditors';
import type { FieldDefinition } from '../../../shared/types';
import {
  GraphProblem, ancestorPath, branchExits, edgesInto, edgesOutOf, extraPathsOf, nearestMint, neighbourOf,
  openPathTails, portsOf, reachable, refuseEdge, runNumbers, sharingSteps, topoOrder,
} from './graph';
import {
  DEFAULT_VIEW, Point, View, fitView, gridStyle, panBy, pinchView, revealRect, toWorld, wheelView,
} from './view';
import { BrandTile } from './BrandIcon';

/**
 * The drawing itself: cards, ports, and the arrows between them.
 *
 * Two things this file is deliberately strict about, both from the design.
 *
 * The DAG is enforced by what the hand can do. A drag that would loop, point
 * upward into an occupied input, or leave an output that already leads
 * somewhere is refused while the mouse is still down, with a reason — never
 * accepted and then complained about, which would mean the canvas briefly drew
 * something that could never run.
 *
 * The run-order numerals are true. They come from the same topological sort
 * with the same y tie-break the engine uses, so dragging a card above its
 * sibling genuinely changes what runs first. If that ever stops being true the
 * numerals become decoration and the page is lying again.
 *
 * R6 (docs/PLAYBOOK-redesign.md §7 R6): the surface is a viewport over a
 * transformed world rather than a scroll box. Dragging the background pans
 * (a click is told from a drag by a 4px threshold), the middle button and
 * Space+drag still pan, ⌘/Ctrl+scroll zooms around the cursor, a plain wheel
 * pans, and two fingers pinch. Every gesture is pointer events with
 * `touch-action: none` and a `pointercancel` handler, so a finger and a mouse
 * are the same code. The arithmetic lives in `view.ts`.
 */

export const CARD = 240;
/** The dot grid the surface draws, and the step a dragged card lands on. */
const GRID = 22;
/** Where a port sits on a card: the middle, or a third in for a branch's two. */
const PORT_X: Record<string, number> = { then: CARD / 2, yes: CARD / 4, no: (CARD * 3) / 4 };
/** A drag this short is a click. */
const DRAG_THRESHOLD = 4;
/** The room each exit of a branch with three or more gets before its card grows. */
export const EXIT_W = 100;
/** A no-ending ghost under one of those exits: narrower than the exit it sits under. */
const COMPACT_GHOST = 92;

/**
 * A card's width: 240, except a branch with more exits than 240 holds.
 *
 * A path per answer can mean six exits, and six labels in 240px are six
 * ellipses — so such a card grows by an exit's width per path. Two exits (every
 * branch drawn before 2026-09-27) is exactly the card it always was.
 */
export function widthOf(node: WorkflowNode): number {
  const exits = portsOf(node).length;
  return node.kind === 'branch' && exits > 2 ? Math.max(CARD, exits * EXIT_W) : CARD;
}

/**
 * Where an output sits along a card's bottom edge: a branch's exits spread
 * evenly under their labels — for two, a quarter and three quarters in, which
 * is `PORT_X` exactly — and everything else from the middle.
 */
export function portX(node: WorkflowNode, port: string): number {
  if (node.kind === 'branch') {
    const ports = portsOf(node);
    const i = ports.indexOf(port);
    if (i >= 0) return (widthOf(node) * (2 * i + 1)) / (2 * ports.length);
  }
  return PORT_X[port] ?? CARD / 2;
}

export interface CanvasNodeInfo {
  connection?: ConnectionRow;
  spec?: CatalogEntry;
}

/** What the toolbar asks the canvas to do to its own view. */
export interface WorkflowCanvasHandle {
  /** Frame every card. */
  fit: () => void;
  /** Pan just enough to bring a card on screen. */
  reveal: (id: string) => void;
  /** Move keyboard focus (and the selection) to a card, revealing it. */
  focusCard: (id: string) => void;
}

export interface WorkflowCanvasProps {
  graph: WorkflowGraph;
  connections: ConnectionRow[];
  catalog: CatalogEntry[];
  fields: FieldDefinition[];
  /** Node ids this form's other pages named — dashed, and not yet real. */
  proposed?: boolean;
  provenance?: Record<string, string>;
  problems?: GraphProblem[];
  /** Which of `problems` block the save — the rest are warnings. */
  errorIds?: Set<GraphProblem>;
  selected: string | null;
  onSelect: (id: string | null) => void;
  onOpen: (id: string) => void;
  /** Absent on a proposal: nothing about it can be edited before it is accepted. */
  onChange?: (next: WorkflowGraph, opts?: { coalesce?: string }) => void;
  /** A rail entry dropped on the surface, in canvas coordinates. */
  onDropEntry?: (x: number, y: number) => void;
  /** Remove a step (and its connection, when nothing else points at it). */
  onDelete?: (id: string) => void;
  /** Rename the step — the connection's own name, or a branch's. */
  onRename?: (id: string) => void;
  /** A second step pointing at the same connection, for the other path. */
  onDuplicate?: (id: string) => void;
  /** Part a step from the others sharing its connection — it gets a copy. */
  onSplit?: (id: string) => void;
  /** Join this step to another without dragging — see ConnectModal. */
  onConnectFrom?: (id: string) => void;
  /** Why this step cannot be removed, or null. The menu omits Delete for it. */
  undeletable?: (id: string) => string | null;
  /** Flip `enabled` or `dry_run` on the step's connection. */
  onToggleConnection?: (id: string, patch: { enabled?: boolean; dry_run?: boolean }) => void;
  /** Turn a no-ending ghost into a real ending, below the node it hangs from. */
  onAddEnding?: (fromId: string, port: string, x: number, y: number) => void;
  /**
   * The reverse of `onConnectFrom` (§4H H4): what should lead TO this step.
   * Passed only under 900, where an orphaned step cannot be wired in by
   * dragging an arrow to it.
   */
  onConnectBefore?: (id: string) => void;
  /**
   * The view (R6): where the drawing sits and how big it is. Controlled when
   * both are given; otherwise the canvas keeps its own, starting at `zoom`.
   */
  view?: View;
  onViewChange?: (v: View) => void;
  /** The starting zoom of an uncontrolled canvas. */
  zoom?: number;
  /** Controls drawn over the surface's top-left corner — the design's pill, zoom and Fit. */
  overlay?: ReactNode;
  formName: string;
  /**
   * How to draw the graph (§4H H2).
   *
   * `'canvas'` is the drawing and is the default, so nothing that does not ask
   * changes. `'steps'` is the same graph as a list in run order, which is what
   * a phone opens on — a 240px card on a 900px surface is not a thing a 375px
   * screen can show.
   */
  mode?: 'canvas' | 'steps';
}

/**
 * A card's height before it has been measured.
 *
 * Edges leave from a card's real bottom edge, and a card's height depends on
 * how long the name and the fact line turn out to be — so the real number is
 * measured after layout (see `heights`). These are only what the first frame
 * draws with; guessing permanently would leave arrows floating away from
 * cards whose text wrapped.
 */
export function assumedHeight(node: WorkflowNode): number {
  if (node.kind === 'start' || node.kind === 'merge') return 36;
  if (node.kind === 'end') return 46;
  if (node.kind === 'branch') return 88;
  return 56;
}

/**
 * An orthogonal path from one port down to another: down, across, down —
 * across at `turn` when one is given, halfway otherwise.
 */
export function edgePath(x1: number, y1: number, x2: number, y2: number, turn?: number): string {
  if (Math.abs(x1 - x2) < 1) return `M${x1} ${y1} V${y2 - 8}`;
  const mid = turn ?? y1 + Math.max(20, (y2 - y1) / 2);
  return `M${x1} ${y1} V${mid} H${x2} V${y2 - 8}`;
}

/**
 * Where each edge leaving a branch with three or more exits turns across.
 *
 * By default every edge from one card turns at the same height, and a branch
 * fanning out to five places drew its arrows as one line — nobody could say
 * which path went where. So each turns at its own height, the one travelling
 * furthest turning first, which is also what keeps a fan from crossing
 * itself. A two-exit branch is not in the map, and draws as it always did.
 */
export function fanTurns(
  graph: WorkflowGraph, ends: (e: WorkflowEdge) => { x1: number; y1: number; x2: number; y2: number }
): Map<WorkflowEdge, number> {
  const turns = new Map<WorkflowEdge, number>();
  for (const node of graph.nodes) {
    if (node.kind !== 'branch' || portsOf(node).length < 3) continue;
    const out = graph.edges.filter((e) => e.from === node.id);
    const travel = (e: WorkflowEdge) => { const p = ends(e); return Math.abs(p.x2 - p.x1); };
    [...out].sort((a, b) => travel(b) - travel(a)).forEach((e, rank) => {
      const { y1, y2 } = ends(e);
      turns.set(e, Math.max(y1 + 12, Math.min(y1 + 16 + rank * 10, y2 - 18)));
    });
  }
  return turns;
}

/** One background gesture in progress: a pan, or a two-finger pinch. */
type Gesture =
  | { kind: 'pan'; start: Point; last: Point; moved: boolean; click: boolean }
  | { kind: 'pinch'; a: number; b: number };

const WorkflowCanvas = forwardRef<WorkflowCanvasHandle, WorkflowCanvasProps>(function WorkflowCanvas({
  graph, connections, catalog, fields, proposed, provenance, problems = [], errorIds = new Set(),
  selected, onSelect, onOpen, onChange, onDropEntry, onDelete, onRename, onDuplicate, onSplit,
  onConnectFrom, onToggleConnection, onAddEnding, onConnectBefore, undeletable, formName,
  view: viewProp, onViewChange, zoom: startZoom, overlay,
  mode = 'canvas',
}, ref) {
  const surface = useRef<HTMLDivElement>(null);
  /** An in-progress connect drag: the port it left and where the mouse is. */
  const [wiring, setWiring] = useState<{ from: string; port: string; x: number; y: number } | null>(null);
  const [refusal, setRefusal] = useState<{ text: string; x: number; y: number } | null>(null);
  const [hoverEdge, setHoverEdge] = useState<number | null>(null);
  /** Which node's ⋯ menu is open. One at a time, like every other row menu. */
  const [menuOn, setMenuOn] = useState<string | null>(null);
  /** The arrow the keyboard would delete, chosen by clicking it. */
  const [pickedEdge, setPickedEdge] = useState<number | null>(null);
  useEffect(() => {
    if (pickedEdge === null || !onChange) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      onChange({ ...graph, edges: graph.edges.filter((_, i) => i !== pickedEdge) });
      setPickedEdge(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickedEdge, graph, onChange]);

  // ---- the view --------------------------------------------------------------
  const [innerView, setInnerView] = useState<View>({ ...DEFAULT_VIEW, zoom: startZoom ?? 1 });
  const view = viewProp ?? innerView;
  // Gesture handlers live on the window and outlive a render, so they read
  // the view through a ref rather than through a stale closure.
  const viewRef = useRef(view);
  viewRef.current = view;
  const setView = (next: View) => {
    viewRef.current = next;
    onViewChange?.(next);
    if (!viewProp) setInnerView(next);
  };

  /** Space turns a left-drag on empty canvas into a pan. */
  const spaceHeld = useRef(false);
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (e.code === 'Space' && tag !== 'INPUT' && tag !== 'TEXTAREA') spaceHeld.current = true;
    };
    const up = (e: KeyboardEvent) => { if (e.code === 'Space') spaceHeld.current = false; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);
  /** Measured card heights, so an arrow always meets the card it points at. */
  const [heights, setHeights] = useState<Record<string, number>>({});
  const measure = (id: string) => (el: HTMLDivElement | null) => {
    if (!el) return;
    // offsetHeight is layout pixels, unaffected by the world's transform,
    // which is the same space the edge geometry is written in.
    const h = el.offsetHeight;
    setHeights((prev) => (prev[id] === h ? prev : { ...prev, [id]: h }));
  };
  const heightOf = (node: WorkflowNode) => heights[node.id] ?? assumedHeight(node);

  const byId = useMemo(() => new Map(graph.nodes.map((n) => [n.id, n])), [graph]);
  const connById = useMemo(() => new Map(connections.map((c) => [c.id, c])), [connections]);
  const specOf = (type?: string) => catalog.find((c) => c.type === type);
  const numbers = useMemo(() => runNumbers(graph), [graph]);
  const live = useMemo(() => reachable(graph), [graph]);
  const problemFor = (id: string) => problems.find((p) => p.nodeId === id);
  /** Errors are supplied first, so the first match is the more serious one. */
  const isError = (p?: GraphProblem) => !!p && errorIds.has(p);

  /** A pointer, in pixels from the surface's top-left. */
  const screenPt = (e: { clientX: number; clientY: number }): Point => {
    const box = surface.current?.getBoundingClientRect();
    return { x: e.clientX - (box?.left || 0), y: e.clientY - (box?.top || 0) };
  };
  /** Pointer position in canvas coordinates, undoing the pan and the zoom. */
  const at = (e: { clientX: number; clientY: number }) => {
    const p = screenPt(e);
    return toWorld(viewRef.current, p.x, p.y);
  };
  /** The surface's size, or null in a layout that has not happened (jsdom). */
  const viewport = () => {
    const box = surface.current;
    if (!box || !box.clientWidth || !box.clientHeight) return null;
    return { w: box.clientWidth, h: box.clientHeight };
  };

  function fit() {
    const size = viewport();
    if (!size) return;
    setView(fitView(graph.nodes.map((n) => ({ x: n.x, y: n.y, w: widthOf(n), h: heightOf(n) })), size.w, size.h));
  }
  function reveal(id: string) {
    const node = byId.get(id);
    const size = viewport();
    if (!node || !size) return;
    const next = revealRect(viewRef.current, { x: node.x, y: node.y, w: widthOf(node), h: heightOf(node) }, size.w, size.h);
    if (next) setView(next);
  }
  /** Move focus to another card, keeping the selection with it. */
  const focusCard = (id: string | null) => {
    if (!id) return;
    const el = surface.current?.querySelector(`[data-card="${id}"]`) as HTMLElement | null;
    el?.focus();
    reveal(id);
  };
  useImperativeHandle(ref, () => ({ fit, reveal, focusCard }));

  // A step chosen from somewhere else — the Outline of problems, a link, the
  // arrow keys — is brought on screen. A click on a card that is already on
  // screen pans nothing.
  useEffect(() => {
    if (selected && mode === 'canvas') reveal(selected);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // The wheel: ⌘/Ctrl zooms around the cursor, a plain wheel pans. A native
  // listener, because React registers `wheel` as passive and a passive
  // listener cannot stop the page from scrolling instead.
  useEffect(() => {
    const box = surface.current;
    if (!box || mode !== 'canvas') return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const p = screenPt(e);
      setView(wheelView(viewRef.current, e, p.x, p.y));
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ---- dragging the background ---------------------------------------------
  /**
   * Pan, pinch, or click — decided by what the pointers do, not by which
   * button started it.
   *
   * A left press on empty canvas starts a pan and is treated as a click only
   * if it ends within 4px of where it began: that click is what clears the
   * selection, exactly as it did before the background could be dragged. The
   * middle button and Space+drag pan too and are never a click. A second
   * finger turns the pan into a pinch; lifting one of the two turns it back.
   */
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const listening = useRef(false);

  function endGesture() {
    gesture.current = null;
    pointers.current.clear();
    if (surface.current) surface.current.style.cursor = '';
    if (listening.current) {
      window.removeEventListener('pointermove', onSurfaceMove);
      window.removeEventListener('pointerup', onSurfaceUp);
      window.removeEventListener('pointercancel', onSurfaceCancel);
      listening.current = false;
    }
  }
  function onSurfaceMove(ev: PointerEvent) {
    const g = gesture.current;
    if (!g || !pointers.current.has(ev.pointerId)) return;
    const p = screenPt(ev);
    if (g.kind === 'pinch') {
      const a0 = pointers.current.get(g.a);
      const b0 = pointers.current.get(g.b);
      pointers.current.set(ev.pointerId, p);
      const a1 = pointers.current.get(g.a);
      const b1 = pointers.current.get(g.b);
      if (a0 && b0 && a1 && b1) setView(pinchView(viewRef.current, a0, b0, a1, b1));
      return;
    }
    pointers.current.set(ev.pointerId, p);
    if (!g.moved) {
      if (Math.abs(p.x - g.start.x) + Math.abs(p.y - g.start.y) < DRAG_THRESHOLD) return;
      g.moved = true;
      if (surface.current) surface.current.style.cursor = 'grabbing';
    }
    setView(panBy(viewRef.current, p.x - g.last.x, p.y - g.last.y));
    g.last = p;
  }
  function onSurfaceUp(ev: PointerEvent) {
    const g = gesture.current;
    if (!g) return;
    pointers.current.delete(ev.pointerId);
    if (g.kind === 'pinch') {
      if (pointers.current.size >= 2) return;
      // One finger left: carry on as a pan, and never as a click.
      const rest = [...pointers.current.entries()][0];
      if (rest) { gesture.current = { kind: 'pan', start: rest[1], last: rest[1], moved: true, click: false }; return; }
      endGesture();
      return;
    }
    const wasClick = g.click && !g.moved;
    endGesture();
    if (wasClick) { setPickedEdge(null); onSelect(null); }
  }
  function onSurfaceCancel(ev: PointerEvent) {
    // A touch the browser took back (a scroll it decided to own, a pen that
    // left the digitiser) never ends in `pointerup`; without this the
    // listeners stay on the window and the next touch anywhere pans.
    pointers.current.delete(ev.pointerId);
    if (!pointers.current.size) endGesture();
  }
  function onSurfaceDown(e: ReactPointerEvent) {
    if (e.button !== 0 && e.button !== 1) return;
    const p = screenPt(e);
    if (gesture.current) {
      // A second finger while one is already down: a pinch.
      pointers.current.set(e.pointerId, p);
      const ids = [...pointers.current.keys()];
      if (ids.length === 2) gesture.current = { kind: 'pinch', a: ids[0], b: ids[1] };
      return;
    }
    // The middle button would otherwise start the browser's autoscroll.
    e.preventDefault();
    pointers.current.set(e.pointerId, p);
    const forced = e.button === 1 || spaceHeld.current;
    gesture.current = { kind: 'pan', start: p, last: p, moved: false, click: !forced };
    if (forced && surface.current) surface.current.style.cursor = 'grabbing';
    if (!listening.current) {
      window.addEventListener('pointermove', onSurfaceMove);
      window.addEventListener('pointerup', onSurfaceUp);
      window.addEventListener('pointercancel', onSurfaceCancel);
      listening.current = true;
    }
  }
  // Nothing is left listening when the canvas goes.
  useEffect(() => () => endGesture(), []);   // eslint-disable-line react-hooks/exhaustive-deps

  // ---- moving a card ------------------------------------------------------
  function startMove(e: ReactPointerEvent, node: WorkflowNode) {
    if (!onChange || e.button !== 0) return;
    e.stopPropagation();
    onSelect(node.id);
    const origin = at(e);
    const from = { x: node.x, y: node.y };
    let moved = false;
    const move = (ev: PointerEvent) => {
      const now = at(ev);
      const dx = now.x - origin.x;
      const dy = now.y - origin.y;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 3) return;
      moved = true;
      // Snap to the dot grid the canvas is already drawing, unless Alt is
      // held. A grid you can see but not land on reads as sloppiness, and the
      // vertical position is RUN ORDER here — two siblings a pixel apart look
      // deliberate and are not.
      const snap = (v: number) => (ev.altKey ? Math.round(v) : Math.round(v / GRID) * GRID);
      onChange({
        ...graph,
        nodes: graph.nodes.map((n) => (n.id === node.id
          ? { ...n, x: Math.max(0, snap(from.x + dx)), y: Math.max(0, snap(from.y + dy)) }
          : n)),
      // One drag is one undo. Without this a person who nudged a card and
      // pressed Ctrl+Z would move it back one pixel.
      }, { coalesce: `move:${node.id}` });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', up);
    };
    // A press that never became a drag is a click, and a click opens the
    // step. The start and a rejoin have nothing to set, so a click only
    // selects them (Enter and a double-click still say why).
    const release = () => {
      up();
      if (!moved && node.kind !== 'start' && node.kind !== 'merge') onOpen(node.id);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', release);
    // A touch that becomes a scroll, or a pen that leaves the digitiser, ends
    // in `pointercancel` and never in `pointerup` — without this the listeners
    // stay on the window and the next touch anywhere drags the card again.
    window.addEventListener('pointercancel', up);
  }

  // ---- drawing an arrow ---------------------------------------------------
  function startWire(e: ReactPointerEvent, node: WorkflowNode, port: string) {
    if (!onChange) return;
    e.stopPropagation();
    e.preventDefault();
    const here = at(e);
    setWiring({ from: node.id, port, x: here.x, y: here.y });
    const move = (ev: PointerEvent) => {
      const now = at(ev);
      setWiring((w) => (w ? { ...w, x: now.x, y: now.y } : w));
    };
    const cancel = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      setWiring(null);
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      setWiring(null);
      // `ev.target` for a touch is the element the gesture STARTED on — the
      // port — because the browser captures it there, so a finger could never
      // land an arrow anywhere. The element under the release point is what
      // was actually aimed at, for a mouse and a finger alike.
      const under = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
      const target = (under || (ev.target as HTMLElement))?.closest('[data-node]')?.getAttribute('data-node');
      if (!target) return;
      const why = refuseEdge(graph, node.id, port, target);
      if (why) {
        // The reason arrives where the drop happened, not in a banner
        // somewhere else on the page.
        setRefusal({ text: why, x: ev.clientX, y: ev.clientY });
        window.setTimeout(() => setRefusal(null), 2600);
        return;
      }
      onChange({ ...graph, edges: [...graph.edges, { from: node.id, port, to: target }] });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  }

  /** What a screen reader says about a card, since the drawing says nothing. */
  function ariaFor(node: WorkflowNode): string {
    const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
    const what = node.kind === 'start' ? 'the submission arriving'
      : node.kind === 'merge' ? 'the paths rejoining'
        : node.kind === 'branch' ? `branch, ${String(node.config?.name || 'which way')}`
          : node.kind === 'end' ? `ending, ${String(node.config?.title || node.config?.mode || 'thank you')}`
            : `${specOf(conn?.type)?.title || node.kind}, ${conn?.name || 'not set up'}`;
    const step = numbers[node.id] ? `step ${numbers[node.id]}: ` : '';
    return `${step}${what}. Enter to open.`;
  }

  function onCardKey(e: ReactKeyboardEvent, node: WorkflowNode) {
    if (e.key === 'Enter') { e.preventDefault(); onOpen(node.id); return; }
    const dir = ({ ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' } as const)[
      e.key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'
    ];
    if (!dir) return;
    e.preventDefault();
    // Alt turns the arrows into a nudge. Vertical position is run order here,
    // so this is a way to reorder siblings without a pointer, not decoration.
    if (e.altKey && onChange) {
      const dx = dir === 'left' ? -GRID : dir === 'right' ? GRID : 0;
      const dy = dir === 'up' ? -GRID : dir === 'down' ? GRID : 0;
      onChange({
        ...graph,
        nodes: graph.nodes.map((n) => (n.id === node.id
          ? { ...n, x: Math.max(0, n.x + dx), y: Math.max(0, n.y + dy) }
          : n)),
      }, { coalesce: `move:${node.id}` });
      return;
    }
    focusCard(neighbourOf(graph, node.id, dir));
  }

  const cutEdge = (index: number) => {
    if (!onChange) return;
    onChange({ ...graph, edges: graph.edges.filter((_, i) => i !== index) });
    setHoverEdge(null);
  };

  // ---- what each card says ------------------------------------------------
  function factOf(node: WorkflowNode): { text: string; tone?: 'bad' | 'warn' | 'from' } | null {
    const problem = problemFor(node.id);
    if (problem) return { text: problem.message, tone: isError(problem) ? 'bad' : 'warn' };
    if (proposed && provenance?.[node.id]) return { text: provenance[node.id], tone: 'from' };

    if (node.kind === 'branch') {
      const extra = extraPathsOf(node);
      if (extra.length) {
        return {
          text: manyPathsFact(
            [node.config?.when, ...extra.map((p) => p.when)],
            (key) => fields.find((f) => f.key === key)?.label || key
          ),
        };
      }
      const group = (node.config?.when || null) as Parameters<typeof describeGroup>[0] | null;
      if (!group?.when?.length) return { text: 'no condition yet — everything takes the second path' };
      return { text: describeGroup(group, fields) };
    }
    if (node.kind === 'chase') {
      const mint = nearestMint(graph, node.id);
      return { text: mint ? `re-sends the link from ↑ ${nameOf(mint)}` : 'no follow-up link above it to re-send' };
    }
    // An ending carries its overline and ONE line, and that line is its name
    // below — the heading it shows, the address it sends to, or the form it
    // continues into. A second line would be the same fact said twice.
    if (node.kind === 'end') return null;
    const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
    if (!conn) return null;
    if (conn.run_when?.when?.length) return { text: describeGroup(conn.run_when, fields) };
    const provides = specOf(conn.type)?.provides;
    if (provides?.length) {
      // Two at most. The point of the fact line is "what does this hand on",
      // and a card listing five tokens has stopped answering that.
      const shown = provides.slice(0, 2).map((p) => `{{${tokenName(conn.type, p.key)}}}`);
      return { text: `publishes ${shown.join(', ')}${provides.length > shown.length ? ' …' : ''}` };
    }
    return null;
  }

  function nameOf(node: WorkflowNode): string {
    if (node.kind === 'start') return 'A submission arrives';
    if (node.kind === 'merge') return 'The paths rejoin';
    if (node.kind === 'branch') return String(node.config?.name || 'Which way?');
    if (node.kind === 'chase') return String(node.config?.name || 'Chase: day 3, day 7');
    if (node.kind === 'end') {
      const cfg = node.config || {};
      if (cfg.mode === 'redirect') {
        // The bare host and path: an ending is read at a glance, and
        // "https://" is the part nobody is checking.
        const url = String(cfg.redirectUrl || '');
        return url ? url.replace(/^https?:\/\//, '') : 'No address yet';
      }
      // A PDF on offer holds the page (CompletionFields), so "straight" is
      // only said when it is what the respondent gets.
      if (cfg.mode === 'continue') return cfg.continueStraight && !cfg.allowDocumentDownload ? 'Straight into the follow-up form' : 'Into the follow-up form';
      // No title is not "the form's own" any more — Settings has no thank-you
      // to defer to. This IS it, and unnamed it is what the public form shows
      // by default.
      return cfg.title ? `“${cfg.title}”` : 'Thank you';
    }
    const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
    return conn?.name || 'Not set up yet';
  }

  function overlineOf(node: WorkflowNode): { text: string; tone?: 'identity' | 'routing' } | null {
    if (node.kind === 'branch') return { text: 'Branch on an answer', tone: 'routing' };
    if (node.kind === 'mint') return { text: 'Private link', tone: 'identity' };
    if (node.kind === 'chase') return { text: 'Reminders', tone: 'identity' };
    if (node.kind === 'end') {
      const mode = node.config?.mode;
      // The purple overline ties a continue ending to the follow-up pair, the
      // same colour the mint and chase cards carry — one story, one hue.
      if (mode === 'continue') return { text: 'Ending — continue', tone: 'identity' };
      return { text: `Ending — ${mode === 'redirect' ? 'redirect' : 'message'}` };
    }
    const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
    return { text: specOf(conn?.type)?.title || conn?.type || 'Step' };
  }

  function tileOf(node: WorkflowNode): { glyph: string; tone?: 'identity' | 'routing' } {
    if (node.kind === 'branch') return { glyph: '?', tone: 'routing' };
    if (node.kind === 'mint') return { glyph: '🔗', tone: 'identity' };
    if (node.kind === 'chase') return { glyph: '⏰', tone: 'identity' };
    if (node.kind === 'end') return { glyph: '⚑' };
    const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
    return { glyph: specOf(conn?.type)?.icon || '🔌' };
  }

  const extent = graph.nodes.reduce(
    (acc, n) => ({ w: Math.max(acc.w, n.x + widthOf(n) + 120), h: Math.max(acc.h, n.y + heightOf(n) + 140) }),
    { w: 900, h: 520 }
  );

  /**
   * The nearest x to `x`, on the row at `y`, where a new ending overlaps no
   * card — a grid step at a time, either way. The endings for a branch's many
   * exits are wider than the exits are apart, so without this each one made
   * would land on the last.
   */
  function clearOfCards(x: number, y: number): number {
    const h = assumedHeight({ id: '', kind: 'end', x: 0, y: 0 });
    const pad = 12;
    const clear = (cx: number) => graph.nodes.every((n) => (
      cx + CARD + pad <= n.x || n.x + widthOf(n) + pad <= cx || y + h + pad <= n.y || n.y + heightOf(n) + pad <= y
    ));
    for (let step = 0; step <= 60; step += 1) {
      for (const cx of step ? [x + step * GRID, x - step * GRID] : [x]) {
        if (cx >= 0 && clear(cx)) return cx;
      }
    }
    return Math.max(0, x);
  }

  /** Where an edge's two ends are, in the world's coordinates. */
  const edgeEnds = (e: WorkflowEdge) => {
    const from = byId.get(e.from);
    const to = byId.get(e.to);
    if (!from || !to) return { x1: 0, y1: 0, x2: 0, y2: 0 };
    return { x1: from.x + portX(from, e.port), y1: from.y + heightOf(from), x2: to.x + widthOf(to) / 2, y2: to.y };
  };
  const turns = fanTurns(graph, edgeEnds);

  /**
   * Where an arrow's × sits on hover: at the corner where it turns, which is
   * on the line for every arrow — and, for one of a fan, just past its own
   * turn, on the stretch that is only its own.
   */
  function edgeMark(e: WorkflowEdge, from: WorkflowNode, to: WorkflowNode): { left: number; top: number } {
    const turn = turns.get(e);
    const y1 = from.y + heightOf(from);
    if (turn === undefined) return { left: from.x + portX(from, e.port), top: y1 + Math.max(20, (to.y - y1) / 2) };
    return { left: to.x + widthOf(to) / 2, top: Math.min(turn + 14, to.y - 14) };
  }

  /**
   * Everything a step's ⋯ offers, built once (§4H H2).
   *
   * The Steps list and the card on the drawing are two ways to reach the same
   * step, so they offer the same actions — literally the same JSX, not a
   * second list that drifts.
   */
  function stepMenuItems(node: WorkflowNode, conn?: ConnectionRow) {
    return (
      <>
        <button onClick={() => onOpen(node.id)}>Configure…</button>
        {onRename && <button onClick={() => onRename(node.id)}>Rename…</button>}
        {onDuplicate && (
          <button onClick={() => onDuplicate(node.id)}>Add another like this</button>
        )}
        {onSplit && sharingSteps(graph, node.id).length > 0 && (
          <button onClick={() => onSplit(node.id)}>Give this step its own settings</button>
        )}
        {onConnectFrom && portsOf(node).length > 0 && (
          <button onClick={() => onConnectFrom(node.id)}>Connect to…</button>
        )}
        {onConnectBefore && node.kind !== 'start' && !edgesInto(graph, node.id).length && (
          <button onClick={() => onConnectBefore(node.id)}>What runs before…</button>
        )}
        {conn && onToggleConnection && (
          <>
            <div className="rule" />
            <button onClick={() => onToggleConnection(node.id, { enabled: !conn.enabled })}>
              {conn.enabled ? 'Switch this step off' : 'Switch this step on'}
            </button>
            <button onClick={() => onToggleConnection(node.id, { dry_run: !conn.dry_run })}>
              {conn.dry_run ? 'Stop test mode — really send' : 'Test mode — build it, send nothing'}
            </button>
          </>
        )}
        {onDelete && !undeletable?.(node.id) && (
          <>
            <div className="rule" />
            <button className="danger" onClick={() => onDelete(node.id)}>Delete this step</button>
          </>
        )}
      </>
    );
  }

  /**
   * The drawing, said as a list (§4H H2).
   *
   * `topoOrder` is the order the engine runs the graph in, so the list IS the
   * run order rather than a rendering of one. A branch's two arms are indented
   * under their labels, and a step both arms lead to comes back out to the
   * trunk — so a person reading down the page reads what happens, in the order
   * it happens, which is the one thing a 900px drawing cannot show at 375.
   */
  function stepRows(): { node?: WorkflowNode; armLabel?: string; depth: number }[] {
    const rows: { node?: WorkflowNode; armLabel?: string; depth: number }[] = [];
    const done = new Set<string>();
    const indeg = new Map<string, number>();
    for (const e of graph.edges) indeg.set(e.to, (indeg.get(e.to) || 0) + 1);

    const walkArm = (node: WorkflowNode, depth: number) => {
      // A step both arms reach belongs to the trunk, not to either arm.
      if (done.has(node.id) || (indeg.get(node.id) || 0) > 1) return;
      emit(node, depth);
      for (const port of portsOf(node)) {
        for (const e of edgesOutOf(graph, node.id, port)) {
          const next = byId.get(e.to);
          if (next) walkArm(next, depth);
        }
      }
    };

    const emit = (node: WorkflowNode, depth: number) => {
      if (done.has(node.id)) return;
      done.add(node.id);
      rows.push({ node, depth });
      if (node.kind !== 'branch') return;
      for (const exit of branchExits(node)) {
        const targets = edgesOutOf(graph, node.id, exit.port)
          .map((e) => byId.get(e.to))
          .filter((n): n is WorkflowNode => !!n);
        if (!targets.length) continue;
        rows.push({ armLabel: exit.label || exit.fallback, depth: depth + 1 });
        for (const t of targets) walkArm(t, depth + 1);
      }
    };

    for (const n of topoOrder(graph)) emit(n, 0);
    return rows;
  }

  if (mode === 'steps') {
    const rows = stepRows();
    return (
      <div className="wf-steps">
        {!rows.length && <div className="wf-steps-empty">Nothing on this workflow yet.</div>}
        {rows.map((r, i) => {
          if (!r.node) return <div key={`a${i}`} className="wf-arm-label">{r.armLabel}</div>;
          const node = r.node;
          const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
          const problem = problemFor(node.id);
          const over = overlineOf(node);
          const tile = tileOf(node);
          const fact = factOf(node);
          return (
            <div
              key={node.id}
              className={`wf-step-row${r.depth ? ' arm' : ''}${conn && !conn.enabled ? ' off' : ''}`}
            >
              <button
                type="button" className="wf-step" aria-label={ariaFor(node)}
                onClick={() => { onSelect(node.id); onOpen(node.id); }}
              >
                {problem && <span className={`wf-badge ${isError(problem) ? 'bad' : 'warn'}`}>!</span>}
                <BrandTile type={conn?.type} fallback={tile.glyph} className={`wf-tile${tile.tone ? ` ${tile.tone}` : ''}`} />
                <span className="wf-text">
                  {over && <span className={`wf-over${over.tone ? ` ${over.tone}` : ''}`}>{over.text}</span>}
                  <span className="wf-name">{nameOf(node)}</span>
                  {fact && <span className={`wf-fact${fact.tone ? ` ${fact.tone}` : ''}`}>{fact.text}</span>}
                </span>
                {conn && !conn.enabled && <span className="fs-pill neutral">off</span>}
                {conn?.enabled && conn.dry_run && <span className="fs-pill caution">test</span>}
                {numbers[node.id] && conn?.enabled && !conn.dry_run && (
                  <span className="wf-num" title={`Runs ${ordinal(numbers[node.id])}`}>{numbers[node.id]}</span>
                )}
              </button>
              {onChange && node.kind !== 'start' && (
                <span className="wf-menu-slot" onClick={(ev) => ev.stopPropagation()}>
                  <RowMenu
                    open={menuOn === node.id} onToggle={(o) => setMenuOn(o ? node.id : null)}
                    label={nameOf(node)}
                  >
                    {node.kind === 'merge'
                      ? (onDelete && <button className="danger" onClick={() => onDelete(node.id)}>Delete this rejoin</button>)
                      : stepMenuItems(node, conn)}
                  </RowMenu>
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="wf-surface wf-viewport"
      ref={surface}
      style={gridStyle(view, GRID)}
      onPointerDown={onSurfaceDown}
      onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
      onDragOver={(e) => { if (onDropEntry) e.preventDefault(); }}
      onDrop={(e) => {
        if (!onDropEntry) return;
        e.preventDefault();
        const p = at(e);
        onDropEntry(Math.max(0, Math.round(p.x - CARD / 2)), Math.max(0, Math.round(p.y - 20)));
      }}
    >
      <div
        className="wf-world"
        style={{ width: extent.w, height: extent.h, transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
      >
        <svg className="wf-edges" width={extent.w} height={extent.h}>
          {graph.edges.map((e, i) => {
            const from = byId.get(e.from);
            const to = byId.get(e.to);
            if (!from || !to) return null;
            const x1 = from.x + portX(from, e.port);
            const y1 = from.y + heightOf(from);
            const x2 = to.x + widthOf(to) / 2;
            const y2 = to.y;
            const hot = hoverEdge === i;
            const picked = pickedEdge === i;
            const stroke = picked ? 'var(--accent)' : hot ? 'var(--ink-700)' : 'var(--edge)';
            const turn = turns.get(e);
            return (
              <g key={`${e.from}-${e.port}-${e.to}`}>
                <path d={edgePath(x1, y1, x2, y2, turn)} stroke={stroke} strokeWidth={hot || picked ? 2.5 : 2} fill="none" />
                <path d={`M${x2 - 4} ${y2 - 8} L${x2 + 4} ${y2 - 8} L${x2} ${y2} Z`} fill={stroke} />
                <path
                  className="hit" fill="none" d={edgePath(x1, y1, x2, y2, turn)}
                  onPointerEnter={() => setHoverEdge(i)}
                  onPointerLeave={() => setHoverEdge((h) => (h === i ? null : h))}
                  onPointerDown={(ev) => {
                    // An arrow is a thing you can select, not only a thing you
                    // can hover — a trackpad user chasing a 22px target while
                    // the tooltip moves is not an interaction.
                    if (!onChange) return;
                    ev.stopPropagation();
                    setPickedEdge(i);
                    onSelect(null);
                  }}
                />
              </g>
            );
          })}
          {wiring && (() => {
            const from = byId.get(wiring.from);
            if (!from) return null;
            return (
              <path
                d={edgePath(from.x + portX(from, wiring.port), from.y + heightOf(from), wiring.x, wiring.y + 8)}
                stroke="var(--accent)" strokeWidth={2.5} strokeDasharray="5 4" fill="none"
              />
            );
          })()}
        </svg>

        {/* No label pill on a branch's arrows (Ben, 2026-09-27): the exits in
            the card's footer already name each path, and each arrow leaves
            from under its own. */}

        {onChange && hoverEdge !== null && (() => {
          const e = graph.edges[hoverEdge];
          const from = e && byId.get(e.from);
          const to = e && byId.get(e.to);
          if (!from || !to) return null;
          return (
            <button
              className="wf-edge-cut" title="Remove this arrow"
              onPointerEnter={() => setHoverEdge(hoverEdge)}
              onPointerLeave={() => setHoverEdge(null)}
              onPointerDown={(ev) => ev.stopPropagation()}
              onClick={(ev) => { ev.stopPropagation(); cutEdge(hoverEdge); }}
              style={edgeMark(e, from, to)}
            >×</button>
          );
        })()}

        {graph.nodes.map((node) => {
          const problem = problemFor(node.id);
          const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
          const fact = factOf(node);
          const over = overlineOf(node);
          const tile = tileOf(node);
          const legal = !!wiring && !refuseEdge(graph, wiring.from, wiring.port, node.id);
          const classes = [
            'wf-node',
            node.kind === 'start' ? 'pill' : '',
            node.kind === 'end' ? 'ending' : '',
            node.kind === 'merge' ? 'rejoin' : '',
            node.kind === 'branch' ? 'routing' : '',
            selected === node.id ? 'selected' : '',
            problem && isError(problem) ? 'bad' : '',
            problem && !isError(problem) ? 'warn' : '',
            proposed ? 'proposed' : '',
            conn && !conn.enabled ? 'off' : '',
            !live.has(node.id) && node.kind !== 'start' ? 'dim' : '',
          ].filter(Boolean).join(' ');
          const width = widthOf(node);

          return (
            <div
              key={node.id} className={classes} data-node={node.id}
              // Only a card that grew says so: every other card is the
              // stylesheet's 240, exactly as before branches had more paths.
              style={{ left: node.x, top: node.y, position: 'absolute', ...(width !== CARD ? { width } : {}) }}
            >
              <div
                className="card"
                ref={measure(node.id)}
                /*
                 * Focusable, because a canvas somebody can only use with a
                 * mouse is a canvas some people cannot use at all. Tab reaches
                 * every step, Enter opens it (the design says so in as many
                 * words), the arrows walk the flow, and Alt+arrow nudges a
                 * card by one grid square. Drawing an EDGE by keyboard is the
                 * one thing still missing — but the rail's click-to-add
                 * attaches below the selection, so a whole flow can be built
                 * without a pointer even so.
                 */
                role="button"
                tabIndex={0}
                aria-label={ariaFor(node)}
                data-card={node.id}
                onFocus={() => onSelect(node.id)}
                onKeyDown={(e) => onCardKey(e, node)}
                onPointerDown={(e) => startMove(e, node)}
                onDoubleClick={() => onOpen(node.id)}
                style={legal ? { borderColor: 'var(--accent)', boxShadow: '0 0 0 4px color-mix(in srgb, var(--accent-ring) 45%, transparent)' } : undefined}
              >
                {problem && <span className={`wf-badge ${isError(problem) ? 'bad' : 'warn'}`}>!</span>}
                {node.kind === 'start' ? 'A submission arrives' : node.kind === 'merge' ? (
                  <>
                    ↳ The paths rejoin
                    {onChange && onDelete && (
                      <span
                        className="wf-menu-slot rejoin"
                        onPointerDown={(ev) => ev.stopPropagation()}
                        onDoubleClick={(ev) => ev.stopPropagation()}
                      >
                        <RowMenu open={menuOn === node.id} onToggle={(o) => setMenuOn(o ? node.id : null)}>
                          <button className="danger" onClick={() => onDelete(node.id)}>Delete this rejoin</button>
                        </RowMenu>
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <div className="wf-body">
                      <BrandTile type={conn?.type} fallback={tile.glyph} className={`wf-tile${tile.tone ? ` ${tile.tone}` : ''}`} />
                      <span className="wf-text">
                        {over && <span className={`wf-over${over.tone ? ` ${over.tone}` : ''}`}>{over.text}</span>}
                        <span className="wf-name">{nameOf(node)}</span>
                        {fact && <span className={`wf-fact${fact.tone ? ` ${fact.tone}` : ''}`}>{fact.text}</span>}
                      </span>
                      {conn && !conn.enabled && <span className="fs-pill neutral" style={{ marginLeft: 'auto' }}>off</span>}
                      {conn?.enabled && conn.dry_run && <span className="fs-pill caution" style={{ marginLeft: 'auto' }}>test</span>}
                      {numbers[node.id] && conn?.enabled && !conn.dry_run && (
                        <span className="wf-num" title={`Runs ${ordinal(numbers[node.id])}`}>{numbers[node.id]}</span>
                      )}
                      {/* On hover the numeral yields to this. Everything a
                          step can be is reachable from here — including
                          deleting one nothing points at, which otherwise has
                          no affordance at all now that the list is gone. */}
                      {onChange && (
                        <span
                          className="wf-menu-slot"
                          onPointerDown={(ev) => ev.stopPropagation()}
                          onDoubleClick={(ev) => ev.stopPropagation()}
                        >
                          <RowMenu
                            open={menuOn === node.id} onToggle={(o) => setMenuOn(o ? node.id : null)}
                            label={nameOf(node)}
                          >
                            {stepMenuItems(node, conn)}
                          </RowMenu>
                        </span>
                      )}
                    </div>
                    {node.kind === 'branch' && <BranchExitsRow node={node} />}
                  </>
                )}
              </div>

              {node.kind !== 'start' && (
                <span
                  className={`wf-port in${legal ? ' legal' : ''}${edgesInto(graph, node.id).length ? ' taken' : ''}`}
                  style={{ left: width / 2 - 5.5 }}
                />
              )}
              {portsOf(node).map((port) => (
                <button
                  key={port} type="button" title={`Drag to the step that runs next`}
                  className={`wf-port out${edgesOutOf(graph, node.id, port).length ? ' taken' : ''}`}
                  style={{ left: portX(node, port) - 5.5 }}
                  onPointerDown={(e) => startWire(e, node, port)}
                />
              ))}
            </div>
          );
        })}

        {/* The fallback, drawn. A path that reaches no ending gets the form's
            own thank-you — a page nobody wrote for that path — and today
            nothing says so anywhere. A warning, not an error. */}
        {onAddEnding && openPathTails(graph).map(({ node, port }) => {
          const x = node.x + portX(node, port) - CARD / 2;
          const y = node.y + heightOf(node) + 46;
          // A branch with three or more exits has them an exit's width apart,
          // and a full ghost under each would sit on its neighbours. Each
          // shrinks to fit under its own exit, the sentence moving to its
          // title — and the ending it makes lands where no card already is.
          if (node.kind === 'branch' && portsOf(node).length > 2) {
            const exits = portsOf(node);
            const exit = branchExits(node).find((b) => b.port === port);
            const words = `No ending on “${exit?.label || exit?.fallback}” — it falls back to the form’s `
              + 'default thank-you. Give it an ending of its own.';
            // The ending it makes: in a row under the branch, one card's width
            // apart and in the exits' order, and low enough for the fan's
            // staggered turns to clear before it — so the arrows read apart.
            const slot = node.x + widthOf(node) / 2 - CARD / 2
              + (exits.indexOf(port) - (exits.length - 1) / 2) * (CARD + 20);
            const below = y + 10 * (exits.length - 1);
            // Under its exit, and never over a card: a card the two-way layout
            // left beneath a branch that has since grown pushes the ghost below
            // it, rather than the ghost hiding it.
            const left = x + CARD / 2 - COMPACT_GHOST / 2;
            let top = y;
            for (let tries = 0; tries < 8; tries += 1) {
              const under = graph.nodes.find((n) => n.x < left + COMPACT_GHOST && n.x + widthOf(n) > left
                && n.y < top + 34 && n.y + heightOf(n) > top);
              if (!under) break;
              top = under.y + heightOf(under) + 12;
            }
            return (
              <button
                key={`ghost-${node.id}-${port}`}
                className="wf-ghost ending compact"
                style={{ left, top }}
                onPointerDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => { ev.stopPropagation(); onAddEnding(node.id, port, clearOfCards(Math.round(slot), below), below); }}
                title={words} aria-label={words}
              >
                <span className="glyph">⚑</span>
                <span>No ending</span>
              </button>
            );
          }
          return (
            <button
              key={`ghost-${node.id}-${port}`}
              className="wf-ghost ending"
              style={{ left: x, top: y }}
              onPointerDown={(ev) => ev.stopPropagation()}
              onClick={(ev) => { ev.stopPropagation(); onAddEnding(node.id, port, x, y); }}
              title="Give this path an ending of its own"
            >
              <span className="glyph">⚑</span>
              <span className="wf-text">
                <span className="wf-over">No ending on this path</span>
                <span className="ghost-fact">falls back to the form’s default thank-you</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* The design's overlay controls, pinned to the surface, never panned. */}
      {overlay && (
        <div className="wf-overlay" onPointerDown={(ev) => ev.stopPropagation()}>{overlay}</div>
      )}
      {onChange && (
        <div className="wf-hint">
          runs top to bottom · among siblings, higher runs first
          {formName ? ` · ${formName}` : ''}
        </div>
      )}
      {proposed && <div className="wf-hint">Proposed — dashed until adopted</div>}
      <div className="wf-hint right wf-desk-only">Drag the background to pan · ⌘/Ctrl + scroll to zoom</div>

      {refusal && (
        <div className="wf-refusal" style={{ left: refusal.x, top: refusal.y }}>{refusal.text}</div>
      )}
    </div>
  );
});

export default WorkflowCanvas;

/**
 * The fact line of a branch with three or more paths, from the conditions of
 * every path but "everything else".
 *
 * Its exits already say WHICH answers, so this says what they are answers
 * to — or, where the paths test different questions and so could overlap,
 * the rule that settles it.
 */
export function manyPathsFact(conditions: unknown[], labelOf: (key: string) => string): string {
  const tested = [...new Set(conditions.flatMap(
    (g) => ((g as { when?: { field?: string }[] } | null)?.when || []).map((r) => r.field || '')
  ).filter(Boolean))];
  const paths = conditions.length + 1;
  if (!tested.length) return 'no conditions yet — everything takes the last path';
  if (tested.length === 1) return `${paths} paths on “${labelOf(tested[0])}”`;
  return `${paths} paths — the first that matches is taken`;
}

/**
 * A branch card's footer: one cell per exit, in the author's words.
 *
 * Two exits are the two-column footer the design draws. Three or more share
 * the (grown) card evenly, with a rule between each — `many`, so the two-exit
 * footer is untouched. `took` marks the exits a run took (RunView), dimming
 * the rest the way an unlabelled exit is dimmed.
 */
export function BranchExitsRow({ node, took }: { node: WorkflowNode; took?: (port: string) => boolean }) {
  const exits = branchExits(node);
  const many = exits.length > 2;
  return (
    <div
      className={`wf-exits${many ? ' many' : ''}`}
      style={many ? { gridTemplateColumns: `repeat(${exits.length}, minmax(0, 1fr))` } : undefined}
    >
      {exits.map((x, i) => (
        <span key={i} className={(took ? took(x.port) : !!x.label) ? '' : 'muted'} title={many ? x.label || x.fallback : undefined}>
          {x.label || x.fallback}
        </span>
      ))}
    </div>
  );
}

/** `freshdesk` + `ticketId` -> `freshdeskTicketId`, mirroring the server. */
const tokenName = (type: string, key: string) =>
  type.replace(/[^a-zA-Z0-9]/g, '') + key.charAt(0).toUpperCase() + key.slice(1);

const ordinal = (n: number) => {
  const names = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth'];
  return names[n] || `${n}th`;
};

export { ancestorPath };
